import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Evolving a poem with an hour of python hacking",
  "description": "",
  "published": "2010-11-30T00:00:00.000Z",
  "redirect_from": ["/blog/evolving-a-poem-with-an-hour-of-python-hacking/swizec/1505"],
  "categories": "Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <ul>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "http://commons.wikipedia.org/wiki/File:Oscar_Wilde_3g07095u.jpg"
          }}><img parentName="a" {...{
              "src": "https://swizec.com/blog/wp-content/uploads/2010/12/300px-Oscar_Wilde_3g07095u.jpg",
              "alt": "Three quarter length portrait of Oscar Wilde (...",
              "title": "Three quarter length portrait of Oscar Wilde (..."
            }}></img></a></p>
        <p parentName="li">{`Image via `}<a parentName="p" {...{
            "href": "http://commons.wikipedia.org/wiki/File:Oscar_Wilde_3g07095u.jpg"
          }}>{`Wikipedia`}</a></p>
      </li>
    </ul>
    <p>{`On Monday I got a little bored at class. This is something that is likely to happen when you're stuck at classes for nearly the whole day.`}</p>
    <p>{`During a break a classmate of mine showed me something cool. It was a Java applet that used genetic `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Algorithm",
        "title": "Algorithm"
      }}>{`algorithms`}</a>{` (we think it was just basic `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Evolution",
        "title": "Evolution"
      }}>{`evolution`}</a>{`) to create a model of a car that can travel over rugged terrain ... Actually thanks to Zemanta I found the link -> `}<a parentName="p" {...{
        "href": "http://www.qubit.devisland.net/ga/"
      }}>{`Go here for a very cool genetic algorithm demo`}</a></p>
    <p>{`This gave me a cool idea to retry making an algorithm I was making a year ago in `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Lisp_%28programming_language%29",
        "title": "Lisp (programming language)"
      }}>{`Lisp`}</a>{`.`}</p>
    <p>{`Because wi-fi wasn't working I wrote it in python this time, simply because I don't need to reference documentation to write python.`}</p>
    <p>{`So I made something cool. (I think) In an hour of solid hacking I created a simple evolutionary algorithm that given enough time can create pretty much any piece of text by performing random `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Mutation",
        "title": "Mutation"
      }}>{`mutations`}</a>{` and breeding strings together. My classmates can verify that it only took an hour.`}</p>
    <p>{`Sure, nothing very impressive on a grand scale of things. I mean, what's cool about taking a bunch of random characters and applying random transformations to them to create a known piece of text right?`}</p>
    <p>{`Well I don't know, but my nerd side finds it incredibly satisfying.`}</p>
    <p>{`Anyway I tried it with `}<a parentName="p" {...{
        "href": "http://en.wikisource.org/wiki/Amor_Intellectualis"
      }}>{`Oscar Wilde's Amor Intellectualis`}</a>{`.`}</p>
    <p>{`You can see the whole code on `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/random-coding/blob/master/evolve-text.py"
      }}>{`github`}</a>{`.`}</p>
    <p>{`To make this post about a little bit more than just geek bravado, I'm going to try explaining why I believe the algorithm works at all and what makes it pretty successful. Then I'm going to explain a better approach that maybe I will implement some day.`}</p>
    <h2 {...{
      "id": "how"
    }}>{`How?`}</h2>
    <p>{`Ok so the basics of evolution algorithms are pretty simple. You take a population of random stuff. With every generation you mutate the members, perform breeding amongst the best members and remove the worst members from the population.`}</p>
    <p>{`Yep, pretty much like natural selection+evolution in nature.`}</p>
    <p>{`Obviously this means that choosing a proper breeding and `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Fitness_function",
        "title": "Fitness function"
      }}>{`fitness functions`}</a>{` is crucial here. The mutation function isn't very interesting ... and in fact as I discovered it's almost better if you don't have it at all. The real reason it's usually there is because it helps the algorithm get out of local minimums.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`def mutate(a):
    return "".join([random.choice(ALPHABET) if abs(random.random()-0.5) < MUTATE_CHANCE else k for k in a])
`}</code></pre>
    <p>{`Basically `}<em parentName="p">{`mutate`}</em>{` just goes through the string and `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Randomness",
        "title": "Randomness"
      }}>{`randomly`}</a>{` changes characters, if some event with a low chance of happening occurs.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`def distance(a, b):
    return sum([1 if a[i]!=b[i] else 0 for i in range(len(a))])
`}</code></pre>
    <p>{`Distance between strings is my fitness function. It is roughly based on my understanding of Leiningen distance. It simply counts the number of characters that differ between two strings. Very basic stuff.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`def compete(population):
    population.sort(key=lambda a: distance(a, TARGET))
    return population
`}</code></pre>
    <p>{`My compete function is pretty basic as well. Because of python's awesomeness and because my fitness function returns a simple integer, I can simply sort the population by distance. Lovely.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`def breed(a, b):
    if random.random()-0.5 < BREED_CHANCE:
        s = random.randint(0, len(a)-10)
        e = s+random.randint(1, BREED_MAX_CHUNK)
        c =  a[0:s]+b[s:e]+a[e:]
        return c
    else:
        return mutate(b)
`}</code></pre>
    <p>{`The breed function is where it gets interesting and I firmly believe the way I implemented this, is the reason the algorithm performs as well as it does ... it's also probably the reason why it doesn't perform better :)`}</p>
    <p>{`As you can see what I'm doing here is I'm taking a string and replacing a random chunk of it with `}<em parentName="p">{`the same`}</em>{` random chunk from a different string. The key thing here is that I'm `}<em parentName="p">{`not`}</em>{` shuffling things around. What this does is that it preserves good candidates in a roughly good state.`}</p>
    <p>{`For example, if you're at only a single character difference from the target text, this type of breeding assures that nothing but that single character will be changing.`}</p>
    <p>{`Also of interest might be how I choose whom to breed. Because the breeding function basically clones a part of a worse subject into a part of a better subject (due to the way I'm calling it) what I do is I choose the best 20 members and breed the best 60 members with them. Roughly this ratio seems to produce the best results.`}</p>
    <p>{`Oh and I had problems with inbreeding. It's funny, but if the population was too small and not enough members are breeding what happens is that every member in the population becomes identical, while none of them match the target. Guess all there's something to all those hillbilly jokes :D`}</p>
    <h2 {...{
      "id": "performance"
    }}>{`Performance`}</h2>
    <p>{`I haven't really put this algorithm through its paces, but I was pleasantly surprised when it only took 10 generations to evolve a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Simple_sentence",
        "title": "Simple sentence"
      }}>{`simple sentence`}</a>{` and only about 150 generations to create a stanza of Hamlet.`}</p>
    <p>{`The Oscar Wilde poem mentioned earlier is taking a bit longer. After 1000 generations it seems to be mostly prancing around the 5-8 differences range and showing a general downward trend. The lowest peak I saw was at 4. It will probably finish eventually, but perhaps some tweaking of the settings is needed.`}</p>
    <p>{`At the time of writing the algorithm has managed to turn this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-random"
      }}>{`llajC
beq'abvnM.pvvTSEjl -S
c ue-v,ub,
lWoqf'nmhpojfWNNufyv-EywDCO,m:ayu.fqOMTbew.hCyiOkp:vlgSffDFun.nyiM'np bwlkbkj'EyST.kN.li -og
EeiqctEi.aSCwcjNSwmpnEamhoeODSc.
,wltnOvMN:oprSbi,CobNh-
iyw..hbpF-NNk'fusCtgtT.upmetwAjenlWOsAEg
AncoilDSvAO.DmMoiyFttAawTiCjaNu
asoCtynkhO-CoDdWWW,gE
iOwuk.yFS v
Nmdqtvs'fgiAS,cWtwOotm:
r,dt,FNqen:f-
y,Aa, pMNrDM
EOFFgqjM
MwmtldkD Sb.eq'D:ncc-vdvkpTme,:,'jDabdnNS'p'bFT
gWhudWprFpFywAh.bdCWOwcNk-fbou,,gWosvfTyFhrkACd CrhfbrDN

oMw'cEfiEcqpqS
pTdpyFT,u',TEl hmhskd,'jrAs
kgOaetibdEEfqaAyd,vhh.s
dp'Wicr: uMfa'Wl,f,WEdumCfgEDgqfhhFfDu:mnbvM'iAWjSw :
hrb-FltrDkt'atC
`}</code></pre>
    <p>{`Into this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-random"
      }}>{`Oft have be trod the vales of Castaly
And heard sweet notes of sylvan music blown
From antique reeds to common folk unknown:
And often launch
d ou  bark upon that sea
Which the nEne Muses hold in empery,
And ploughed free furrows through the wave and foam,
Nor spread reluctant sail for more safe home
Till we had freighted well our argosy.

Of which despoiled treasures these remain,
Sor:ello's passion, and the honied line
Of young Endymion, lordly Tamburlaine
Driving his pampered jades, and more than these,
The seven-fold vision of the Florentine,
And grave-browed Milton's solemn harmonies.
`}</code></pre>
    <h2 {...{
      "id": "better-idea"
    }}>{`Better idea`}</h2>
    <p>{`Since this simple hack got me interested in this field, I think I've come up with an even better way of creating texts. One that could perhaps even be capable of creating works of art not known in advance and thus become actually a useful implementation of an evolution algorithm.`}</p>
    <p>{`The general idea is such: instead of mixing around random characters and comparing them to a text. What if we would first use this same algorithm to generate words roughly fitting the English syllabic structure.`}</p>
    <p>{`Then we could start putting those words together using a similar principle as I've used here. Just make sure when breeding you are working on the level of words. Then your fitness function basically checks for proper poetic structure.`}</p>
    <p>{`Voila!`}</p>
    <p>{`Not sure that would be at all useful, but it might just be something pretty cool and nerdy to make some day.`}</p>
    <p>{`Comments regarding the suggested algorithm especially wanted!`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.qubit.devisland.net/ga/"
        }}>{`Genetic algorithm demo: finding optimal vehicle design (in Flash)`}</a>{` (qubit.devisland.net)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.boingboing.net/2010/11/30/robots-designed-by-g.html"
        }}>{`Robots designed by genetic algorithms`}</a>{` (boingboing.net)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/1538235/"
        }}>{`What problems have you solved using genetic algorithms/genetic programming?`}</a>{` (stackoverflow.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=105348b0-9f25-43ec-b351-f11f53a0dc1b",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      